import React, { useMemo, useState } from 'react';
import { Box, Typography, Paper, Avatar, Divider, Collapse, IconButton, Card, CardContent } from '@mui/material';
import { ExpandMore, ExpandLess, Person, ChatBubbleOutlineOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const SYSTEM_RE = /^(entrou|saiu)\s+(na|da)\s+conversa\.?$/i;

// Faz o parse de uma transcrição de chat do Movidesk (linhas "DD/MM/AAAA HH:MM - Nome: msg").
function parseChat(raw) {
  let text = (raw || '').replace(/🗨️?\s*Transcrição de chat[^\n]*/g, '').trim();

  let timezone = null;
  const tzMatch = text.match(/Fuso hor[aá]rio:\s*([^\d]*?)\s*(?=\d{2}\/\d{2}\/\d{4})/i);
  if (tzMatch) {
    timezone = tzMatch[1].trim();
    text = text.slice(tzMatch.index + tzMatch[0].length);
  }

  const re = /(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2}(?::\d{2})?)\s*-\s*([^:]+?):/g;
  const bounds = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    bounds.push({ idx: m.index, contentStart: re.lastIndex, date: m[1], time: m[2], sender: m[3].trim() });
  }

  const messages = [];
  for (let i = 0; i < bounds.length; i += 1) {
    const b = bounds[i];
    const end = i + 1 < bounds.length ? bounds[i + 1].idx : text.length;
    const content = text.slice(b.contentStart, end).replace(/\s+/g, ' ').trim();
    messages.push({ date: b.date, time: b.time, sender: b.sender, content });
  }

  return { timezone, messages };
}

const PALETTE_KEYS = ['primary', 'success', 'info', 'warning', 'secondary', 'error'];

const ChatTranscript = ({ text }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const { timezone, messages } = useMemo(() => parseChat(text), [text]);

  const senderColors = useMemo(() => {
    const map = {};
    let idx = 0;
    messages.forEach((msg) => {
      if (!map[msg.sender]) {
        const key = PALETTE_KEYS[idx % PALETTE_KEYS.length];
        map[msg.sender] = theme.palette[key]?.main || theme.palette.text.primary;
        idx += 1;
      }
    });
    return map;
  }, [messages, theme.palette]);

  if (!messages.length) {
    return <Box sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{text}</Box>;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        overflow: 'hidden',
        border: `2px solid ${theme.palette.primary.main}`,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}>
            <ChatBubbleOutlineOutlined />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Transcrição do Chat
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Importada do Movidesk{timezone ? ` · ${timezone}` : ''} · {messages.length} mensagens
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={() => setExpanded(!expanded)} sx={{ color: 'white' }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ p: 2, backgroundColor: theme.palette.background.default }}>
          <Box
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { backgroundColor: theme.palette.background.default },
              '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.divider, borderRadius: '4px' },
            }}
          >
            {messages.map((msg, i) => {
              if (SYSTEM_RE.test(msg.content)) {
                return (
                  <Box key={i} sx={{ textAlign: 'center', my: 1 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontStyle: 'italic' }}>
                      {msg.sender} {msg.content.toLowerCase()} · {msg.time}
                    </Typography>
                    <Divider sx={{ mt: 0.5 }} />
                  </Box>
                );
              }
              const color = senderColors[msg.sender] || theme.palette.grey[500];
              return (
                <Card key={i} sx={{ mb: 1.5, borderLeft: `4px solid ${color}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar sx={{ width: 30, height: 30, backgroundColor: color, color: 'white', fontSize: 14 }}>
                        {(msg.sender || '?').trim().charAt(0).toUpperCase() || <Person sx={{ fontSize: 18 }} />}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {msg.sender}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {msg.date} {msg.time}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}>
                      {msg.content}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ChatTranscript;
